import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Router,
  Users,
  CreditCard,
  Ticket,
  Headphones,
  UserCog,
  LucideIcon
} from 'lucide-react';

interface MobileNavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export const MobileNavigation = React.memo(function MobileNavigation({ 
  currentPage, 
  onPageChange 
}: MobileNavigationProps) {
  const { user } = useAuth();

  const baseNavItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard
    },
    {
      id: 'routers',
      label: 'Routers',
      icon: Router
    },
    {
      id: 'customers',
      label: 'Users',
      icon: Users
    },
    {
      id: 'service-plans',
      label: 'Service Plans',
      icon: CreditCard
    },
    {
      id: 'vouchers',
      label: 'Vouchers',
      icon: Ticket
    },
    {
      id: 'support-tickets',
      label: 'Support',
      icon: Headphones
    }
  ];

  const adminOnlyItems = [
    {
      id: 'accounts',
      label: 'Accounts',
      icon: UserCog
    }
  ];

  const navItems = user?.role === 'admin' 
    ? [...baseNavItems, ...adminOnlyItems]
    : baseNavItems;

  // Ensure we always show exactly 6 items for the 3x2 grid by padding if necessary
  const gridItems: Array<{ id: string; label: string; icon: LucideIcon } | null> = [...navItems];
  while (gridItems.length < 6) {
    gridItems.push(null);
  }

  return (
    <div className="grid grid-cols-3 gap-3 mb-6">
      {gridItems.slice(0, 6).map((item, index) => {
        if (!item) {
          // Empty placeholder for grid alignment
          return <div key={`placeholder-${index}`} className="h-16" />;
        }

        const Icon = item.icon;
        const isActive = currentPage === item.id;
        
        return (
          <button
            key={item.id}
            onClick={() => onPageChange(item.id)}
            className={`
              h-16 flex flex-col items-center justify-center p-2 rounded-lg
              transition-all duration-200 border relative overflow-hidden
              ${isActive 
                ? 'text-white border-[var(--sw-brand)] shadow-md scale-95' 
                : 'text-muted-foreground border-border/50 hover:border-[var(--sw-brand)] hover:text-[var(--sw-brand)] hover:shadow-sm active:scale-95'
              }
            `}
            style={isActive ? {
              backgroundColor: 'var(--sw-brand)'
            } : {}}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'var(--sw-brand-light)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = '';
              }
            }}
          >
            <Icon className="w-5 h-5 mb-1" />
            <span className="text-xs text-center leading-tight font-medium">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
});